import { useState } from "react"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Download, Trash2, FileText, ShieldAlert, Mail } from "lucide-react"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog"
import { supabase } from "@/integrations/supabase/client"
import { useAuth } from "@/hooks/useAuth"
import { toast } from "sonner"
import { Link } from "react-router-dom"

export const PrivacyDataTab = () => {
  const { profile, signOut } = useAuth() as any
  const [exporting, setExporting] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const handleExport = async () => {
    if (!profile?.id) return
    setExporting(true)
    try {
      const { data, error } = await supabase.functions.invoke("export-user-data", { body: {} })
      if (error) throw error
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" })
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = `meus-dados-itacondo-${new Date().toISOString().slice(0,10)}.json`
      a.click()
      URL.revokeObjectURL(url)
      toast.success("Seus dados foram exportados.")
    } catch (e: any) {
      toast.error("Erro ao exportar dados: " + (e?.message ?? "desconhecido"))
    } finally {
      setExporting(false)
    }
  }

  const handleDelete = async () => {
    setDeleting(true)
    try {
      const { error } = await supabase.rpc("anonymize_user_data", {
        target_user_id: profile.id,
        reason_text: "Solicitação do titular via Central de Privacidade",
      } as any)
      if (error) throw error
      toast.success("Sua conta foi anonimizada. Você será desconectado.")
      setTimeout(() => { signOut?.(); window.location.href = "/" }, 1500)
    } catch (e: any) {
      toast.error("Erro ao excluir conta: " + (e?.message ?? "desconhecido"))
      setDeleting(false)
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5" /> Seus direitos como titular dos dados</CardTitle>
          <CardDescription>
            Em conformidade com o art. 18 da LGPD, você pode exercer aqui os seus direitos sobre os dados pessoais que tratamos.
          </CardDescription>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Download className="h-5 w-5" /> Exportar meus dados</CardTitle>
          <CardDescription>Baixe uma cópia completa, em formato legível por máquina (JSON), de todos os seus dados pessoais.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={handleExport} disabled={exporting}>{exporting ? "Gerando..." : "Baixar meus dados"}</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Mail className="h-5 w-5" /> Falar com o Encarregado (DPO)</CardTitle>
          <CardDescription>Solicitações de correção, oposição, portabilidade ou reclamações.</CardDescription>
        </CardHeader>
        <CardContent>
          <Link to="/encarregado-de-dados"><Button variant="outline">Abrir formulário do DPO</Button></Link>
        </CardContent>
      </Card>

      <Card className="border-red-200 dark:border-red-900">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-600"><ShieldAlert className="h-5 w-5" /> Excluir minha conta</CardTitle>
          <CardDescription>
            Anonimiza imediatamente seus dados pessoais. Registros com obrigação legal de guarda (folha de ponto, financeiro) são mantidos
            sem identificação. Esta ação é irreversível.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" disabled={deleting}>
                <Trash2 className="h-4 w-4 mr-2" /> Excluir minha conta
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                <AlertDialogDescription>
                  Tem certeza? Esta ação anonimiza permanentemente seu perfil, encerra suas sessões e remove notificações.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                  Sim, excluir
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>
    </div>
  )
}

export default PrivacyDataTab
